
import { useState, useRef, useEffect } from 'react';

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

interface ChatWidgetProps {
  apiEndpoint?: string;
  apiKey?: string;
  agentName?: string;
  welcomeMessage?: string;
}

export default function ChatWidget({
  apiEndpoint = '',
  apiKey = '',
  agentName = 'Insurance Assistant',
  welcomeMessage = 'Hi! I\'m your Insurance Assistant. How can I help you with your insurance needs today?'
}: ChatWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: welcomeMessage,
      isUser: false,
      timestamp: new Date()
    }
  ]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!inputText.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputText,
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsTyping(true);

    try {
      // If you have your own AI API endpoint, replace this section
      let botResponse = '';
      
      if (apiEndpoint && apiKey) {
        // Custom AI API call
        const response = await fetch(apiEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${apiKey}`
          },
          body: JSON.stringify({
            message: inputText,
            context: 'insurance_agent'
          })
        });
        
        const data = await response.json();
        botResponse = data.response || data.message || 'Sorry, I encountered an error.';
      } else {
        // Fallback responses for demo purposes
        await new Promise(resolve => setTimeout(resolve, 1000));
        botResponse = getInsuranceResponse(inputText);
      }

      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: botResponse,
        isUser: false,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: 'Sorry, I\'m having trouble connecting right now. Please try again later or call us at (949) 951-8119.',
        isUser: false,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const getInsuranceResponse = (input: string): string => {
    const lowerInput = input.toLowerCase();
    
    if (lowerInput.includes('auto') || lowerInput.includes('car')) {
      return 'I can help you with auto insurance! We offer comprehensive coverage including liability, collision, and comprehensive. Would you like a quote or have questions about your current policy?';
    } else if (lowerInput.includes('health') || lowerInput.includes('medical')) {
      return 'Health insurance is crucial for your wellbeing. We can help you find the right plan that covers your medical needs and budget. What specific health coverage are you looking for?';
    } else if (lowerInput.includes('life')) {
      return 'Life insurance provides financial security for your loved ones. We offer term and whole life policies. How much coverage are you considering?';
    } else if (lowerInput.includes('home') || lowerInput.includes('house')) {
      return 'Home insurance protects your most valuable asset. We cover property damage, theft, and liability. Do you need coverage for a new home or want to review your current policy?';
    } else if (lowerInput.includes('quote') || lowerInput.includes('price')) {
      return 'I\'d be happy to help you get a quote! For the most accurate pricing, I recommend scheduling a consultation with one of our insurance doctors. Would you like me to connect you with them?';
    } else if (lowerInput.includes('claim')) {
      return 'For claims assistance, please call our 24/7 claims hotline or access your patient portal. I can also help guide you through the initial steps. What type of claim do you need to file?';
    } else if (lowerInput.includes('hours') || lowerInput.includes('contact')) {
      return 'Our office hours are Monday-Friday 9AM-5PM. We\'re also available after hours and weekends by appointment. You can reach us at (949) 951-8119 or visit us at 13844 Alton Pkwy Suite 132, Irvine, CA 92618.';
    } else {
      return 'Thank you for your question! As your Insurance Assistant, I\'m here to help with all your insurance needs. Could you tell me more about what specific coverage or information you\'re looking for?';
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Chat Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full shadow-2xl transition-all duration-300 hover:scale-110 cursor-pointer ${
          isOpen 
            ? 'bg-red-500 hover:bg-red-600' 
            : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
        }`}
      >
        <div className="w-6 h-6 flex items-center justify-center mx-auto">
          {isOpen ? (
            <i className="ri-close-line text-white text-2xl"></i>
          ) : (
            <i className="ri-customer-service-2-line text-white text-2xl"></i>
          )}
        </div>
        {!isOpen && (
          <div className="absolute -top-2 -right-2 w-4 h-4 bg-green-500 rounded-full animate-pulse"></div>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-50 w-96 h-[500px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden">
          {/* Chat Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <i className="ri-robot-line text-white text-xl"></i>
              </div>
              <div>
                <h3 className="font-semibold text-sm">{agentName}</h3>
                <p className="text-blue-100 text-xs">Online • Instant replies</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="w-8 h-8 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer"
            >
              <i className="ri-subtract-line text-white"></i>
            </button>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.isUser ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-2xl ${
                    message.isUser
                      ? 'bg-blue-600 text-white rounded-br-sm'
                      : 'bg-white text-gray-800 rounded-bl-sm shadow-sm border'
                  }`}
                >
                  <p className="text-sm leading-relaxed">{message.text}</p>
                  <p className={`text-xs mt-1 ${
                    message.isUser ? 'text-blue-100' : 'text-gray-500'
                  }`}>
                    {message.timestamp.toLocaleTimeString([], { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </p>
                </div>
              </div>
            ))}
            
            {/* Typing Indicator */}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-800 p-3 rounded-2xl rounded-bl-sm shadow-sm border">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-4 border-t bg-white">
            <div className="flex space-x-2">
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                disabled={isTyping}
              />
              <button
                onClick={sendMessage}
                disabled={!inputText.trim() || isTyping}
                className="w-10 h-10 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-full flex items-center justify-center transition-colors cursor-pointer disabled:cursor-not-allowed"
              >
                <i className="ri-send-plane-line text-lg"></i>
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              Powered by The Insurance Doctors AI
            </p>
          </div>
        </div>
      )}
    </>
  );
}
